import { lazy, Suspense } from "react";
import { BrandIcon } from "@/components/brand-icon";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { eyebrowClass, headingClass, PageHeader, pageHeadingClass, Shell } from "./layout";

const RentBreakdownTool = lazy(() => import("./RentBreakdownTool").then((module) => ({ default: module.RentBreakdownTool })));
const StartupsPage = lazy(() => import("./StartupsPage").then((module) => ({ default: module.StartupsPage })));
const TokenDashboard = lazy(() => import("./tokens/TokenDashboard"));

const posts = [
  {
    slug: "hello-winterr-dev",
    title: "hello winterr.dev",
    date: "2026-04-29",
    description: "The first note on the new static site.",
  },
];

import { tools, type Tool } from "./catalog";

const cardLinkClass =
  "block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

const BrandCluster = ({ brands }: { brands: string[] }) => (
  <div className="flex items-center gap-2">
    {brands.map((brand) => (
      <span
        className="inline-flex size-7 items-center justify-center rounded-md bg-muted/60 ring-1 ring-border"
        key={brand}
      >
        <BrandIcon className="size-4" name={brand} />
      </span>
    ))}
  </div>
);

const ToolCard = ({ tool }: { tool: Tool }) => (
  <a className={cardLinkClass} href={tool.href}>
    <Card className="h-full transition-colors hover:bg-accent/50">
      <CardHeader>
        <BrandCluster brands={tool.brands} />
        <p className="mt-3 text-xs text-muted-foreground">{tool.eyebrow}</p>
        <CardTitle className="text-xl">{tool.title}</CardTitle>
        <CardDescription>{tool.description}</CardDescription>
      </CardHeader>
    </Card>
  </a>
);

const Home = () => (
  <Shell>
    <section className="grid min-h-[calc(60svh-4.5rem)] max-w-5xl items-center border-b py-14 md:py-0">
      <div>
        <p className={eyebrowClass}>Usage and notes</p>
        <h1 className={headingClass}>winterr.dev</h1>
        <p className="max-w-3xl text-lg leading-8 text-muted-foreground">
          AI usage, notes, and a few things worth following.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Button nativeButton={false} render={<a href="/tokens" />}>
            View token usage
          </Button>
          <Button nativeButton={false} render={<a href="/tools" />} variant="outline">
            Explore
          </Button>
        </div>
      </div>
    </section>
    <section className="grid gap-4 py-8 md:grid-cols-2 xl:grid-cols-3">
      {tools.map((tool) => (
        <ToolCard key={tool.href} tool={tool} />
      ))}
    </section>
  </Shell>
);

const Blog = () => (
  <Shell>
    <PageHeader eyebrow="Markdown pages" title="Blog" />
    <section className="grid gap-3">
      {posts.map((post) => (
        <a className={cardLinkClass} href={`/blog/${post.slug}`} key={post.slug}>
          <Card className="h-full transition-colors hover:bg-accent/50">
            <CardHeader>
              <p className="text-xs text-muted-foreground">{post.date}</p>
              <CardTitle className="text-xl">{post.title}</CardTitle>
              <CardDescription>{post.description}</CardDescription>
            </CardHeader>
          </Card>
        </a>
      ))}
    </section>
  </Shell>
);

const BlogPost = ({ slug }: { slug: string }) => {
  const post = posts.find((item) => item.slug === slug);
  if (!post) return <NotFound />;
  return (
    <Shell>
      <article className="max-w-3xl py-14">
        <p className={eyebrowClass}>{post.date}</p>
        <h1 className={pageHeadingClass}>{post.title}</h1>
        <p className="text-lg leading-8 text-muted-foreground">{post.description}</p>
        <p className="text-lg leading-8 text-muted-foreground">
          A place for public AI usage and technical notes.
        </p>
      </article>
    </Shell>
  );
};

const Tools = () => (
  <Shell>
    <PageHeader eyebrow="Tools" title="Explore">
      Usage, notes, and watchlists.
    </PageHeader>
    <section className="grid gap-4 py-8 md:grid-cols-2 xl:grid-cols-3">
      {tools.map((tool) => (
        <ToolCard key={tool.href} tool={tool} />
      ))}
    </section>
  </Shell>
);

const NotFound = () => (
  <Shell>
    <PageHeader eyebrow="404" title="Page not found">
      That route is not in the static build yet.
    </PageHeader>
  </Shell>
);

const ToolFallback = ({ eyebrow, title }: { eyebrow: string; title: string }) => (
  <section className="py-14 pb-6">
    <p className={eyebrowClass}>{eyebrow}</p>
    <h1 className={pageHeadingClass}>{title}</h1>
  </section>
);

export const App = () => {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  const isRentHost = window.location.hostname === "rent.winterr.dev";
  if (isRentHost || path === "/tools/rent-breakdown") {
    return (
      <Shell>
        <Suspense fallback={<ToolFallback eyebrow="Private ledger" title="Rent breakdown" />}>
          <RentBreakdownTool />
        </Suspense>
      </Shell>
    );
  }
  if (path === "/") return <Home />;
  if (path === "/blog") return <Blog />;
  if (path.startsWith("/blog/")) return <BlogPost slug={path.replace("/blog/", "")} />;
  if (path === "/tools") return <Tools />;
  if (path === "/tokens") {
    return (
      <Shell>
        <Suspense fallback={<ToolFallback eyebrow="Public aggregates" title="Token intelligence" />}>
          <TokenDashboard />
        </Suspense>
      </Shell>
    );
  }
  if (path === "/startups") {
    return (
      <Shell>
        <Suspense fallback={<ToolFallback eyebrow="Loading" title="Interesting startups" />}>
          <StartupsPage />
        </Suspense>
      </Shell>
    );
  }
  return <NotFound />;
};
